import React, { useState, useMemo } from "react";
import { McNotification } from "@maersk-global/mds-react-wrapper";
import { McTag } from "@maersk-global/mds-react-wrapper/components-core/mc-tag";
import { McIcon } from "@maersk-global/mds-react-wrapper/icons";
import axios from "axios";
import { renderWithTooltip ,formatDateAndTime} from "../../../utils/helper";
import { useStore } from "../../../store/store.context";
import { API_URL } from "./../../../services/api-config";
import EmailPreviewModal from "./EmailPreviewModal";
import MessagesModal from '../../shared/MessagesModal';
import EmailViewModal from '../../shared/EmailViewModal';
import CaseHistoryModal from '../../advanced-case-search/CaseHistoryModal';

import "./case-details-header.scss";

const CaseDetailsHeader = ({ caseDetailsInfo, onBackClick }) => {
  const { store, updateStore } = useStore();
  
  const {
    casenumber: id,
    casestatus: originalStatus,
    casestatusreason: originalReason,
    createddate,
  } = caseDetailsInfo;
  const status = (store.detailsStatus || originalStatus || '').toString();
  const casestatusreason = store.caseStatusReason || originalReason;
  const lastModifiedBy = (store.lastModifiedBy || caseDetailsInfo?.modifiedby || caseDetailsInfo?.lastmodifiedby || '').toString();

  const [notification, setNotification] = useState(null);
  const [isEmailModalOpen, setIsEmailModalOpen] = useState(false);
  // Messages modal state (similar behavior to AdvancedCaseSearch)
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [messagesModalOpen, setMessagesModalOpen] = useState(false);
  const [messagesData, setMessagesData] = useState(null);
  const [lastEmail, setLastEmail] = useState(null);
  // Case history modal state
  const [historyModalOpen, setHistoryModalOpen] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyData, setHistoryData] = useState(null);
  // Email view from history activities
  const [unreadModalOpen, setUnreadModalOpen] = useState(false);
  const [unreadModalData, setUnreadModalData] = useState(null);
  const [emailViewOpen, setEmailViewOpen] = useState(false);
  const [emailViewData, setEmailViewData] = useState(null);

  // Fetch last email from email history to display a small preview
  React.useEffect(() => {
    let mounted = true;
    const fetchLastEmail = async () => {
      console.log('CaseDetailsHeader: fetchLastEmail effect running, caseDetailsInfo:', caseDetailsInfo, 'id:', id);
      if (!id) {
        console.log('CaseDetailsHeader: no case id available yet, skipping fetch');
        return;
      }
      try {
        setMessagesLoading(true);
        const response = await axios.get(
          `${API_URL}/email-logs/${id}`,
          {
            headers: {
              "Authorization": `Bearer ${store.authToken}`,
            },
          }
        );
        
        // Extract email_logs from the nested response structure
        const emailLogsData = response.data?.body?.email_logs?.body?.email_logs || [];
        const logs = Array.isArray(emailLogsData) ? emailLogsData : [];
        
        if (logs.length > 0) {
          // Sort by created_at timestamp to get the latest email
          const sortedLogs = logs.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
          const latestEmail = sortedLogs[0];
          
          // Transform to match the expected format
          const transformedEmail = {
            from: latestEmail.sender_email || 'AFRDigitalAssistant@maersk.com',
            to: latestEmail.customer_email,
            sender: latestEmail.sender_email,
            to_recipients: latestEmail.customer_email,
            subject: latestEmail.email_subject,
            created_at: latestEmail.created_at,
            template_id: latestEmail.template_id,
            bcc: latestEmail.bcc,
          };
          
          if (mounted) setLastEmail(transformedEmail);
        } else {
          if (mounted) setLastEmail(null);
        }
      } catch (err) {
        console.error('Error fetching last email:', err);
        if (mounted) setLastEmail(null);
      } finally {
        if (mounted) setMessagesLoading(false);
      }
    };
    fetchLastEmail();
    return () => { mounted = false; };
  }, [id]);

  // Clear any stale reason and last modifier when casenumber changes (do NOT clear detailsStatus here)
  React.useEffect(() => {
    updateStore({ caseStatusReason: "", lastModifiedBy: "" });
  }, [id, updateStore]);

  // Format date/time similar to AdvancedCaseSearch formatDateTime
  const formatDateTime = (val) => {
    if (val == null) return "";
    if (val instanceof Date && !Number.isNaN(val.getTime())) {
      const y = val.getFullYear();
      const m = String(val.getMonth() + 1).padStart(2, '0');
      const d = String(val.getDate()).padStart(2, '0');
      const hh = String(val.getHours()).padStart(2, '0');
      const mm = String(val.getMinutes()).padStart(2, '0');
      const ss = String(val.getSeconds()).padStart(2, '0');
      return `${y}-${m}-${d} ${hh}:${mm}:${ss}`;
    }
    const s = String(val).trim();
    const isoMatch = s.match(/^(\d{4}-\d{2}-\d{2})[T ](\d{2}:\d{2}:\d{2})/);
    if (isoMatch) return `${isoMatch[1]} ${isoMatch[2]}`;
    const parsed = new Date(s);
    if (!Number.isNaN(parsed.getTime())) {
      const y = parsed.getFullYear();
      const m = String(parsed.getMonth() + 1).padStart(2, '0');
      const d = String(parsed.getDate()).padStart(2, '0');
      const hh = String(parsed.getHours()).padStart(2, '0');
      const mm = String(parsed.getMinutes()).padStart(2, '0');
      const ss = String(parsed.getSeconds()).padStart(2, '0');
      return `${y}-${m}-${d} ${hh}:${mm}:${ss}`;
    }
    return s;
  };

  const getSafeLastEmailDateTime = (val) => {
    if (val == null) return 'N/A';

    // Normalise to a trimmed ISO string so validation and formatting
    // both operate on the same timestamp value.
    const normalized = val instanceof Date ? val.toISOString() : String(val).trim();

    if (normalized === '') return 'N/A';

    // Mirror the "append Z" logic used inside formatDateAndTime so that
    // new Date() interprets the timestamp with the same UTC assumption.
    const utcString = normalized.endsWith('Z') ? normalized : normalized + 'Z';
    const parsed = new Date(utcString);
    if (Number.isNaN(parsed.getTime())) return 'N/A';

    let safeDateTime = 'N/A';
    try {
      safeDateTime = formatDateAndTime(normalized);
    } catch (err) {
      safeDateTime = 'N/A';
    }

    return safeDateTime;
  };

  const lastEmailDateTime = getSafeLastEmailDateTime(lastEmail?.created_at);

  const fetchCaseHistory = async () => {
    if (!id) return;
    try {
      setHistoryModalOpen(true);
      setHistoryLoading(true);
      setHistoryData(null);
      const res = await fetch(`${API_URL}/get_case_history?casenumber=${encodeURIComponent(id)}`);
      if (!res.ok) {
        const text = await res.text();
        throw new Error(`Failed to fetch history: ${res.status} ${text}`);
      }
      const json = await res.json();
      const rawHistory = json.history ?? json ?? [];
      const historyArray = Array.isArray(rawHistory) ? rawHistory.slice() : [];
      historyArray.sort((a, b) => {
        const da = a?.datetime ? new Date(a.datetime) : new Date(0);
        const db = b?.datetime ? new Date(b.datetime) : new Date(0);
        return db - da;
      });
      setHistoryData(historyArray);
    } catch (err) {
      setHistoryData({ error: err.message || 'Unknown error' });
    } finally {
      setHistoryLoading(false);
    }
  };
//   const handleCloseTask = async () => {
//   try {
//     const response = await axios.put(
//       `${CORE_URL}/close_import_freight_task`,
//       { casenumber: id },
//       {
//         headers: {
//           "Content-Type": "application/json",
//           "Authorization": `Bearer ${store.authToken}`,
//         },
//       }
//     );
//     setNotification({
//       type: "success",
//       message: `Task closed: ${JSON.stringify(response.data.close_responses)}`,
//     });
//   } catch (error) {
//     setNotification({
//       type: "error",
//       message: error.response?.data?.detail
//         ? `Failed: ${error.response.data.detail}`
//         : `Error: ${error.message}`,
//     });
//   }
//   setTimeout(() => setNotification(null), 3000);
// };


// const handleSendEmailMissingDocuments = () => {
//   setIsEmailModalOpen(true);
// };

// const handleEmailModalClose = () => {
//   setIsEmailModalOpen(false);
// };

// const handleEmailSent = () => {
//   setNotification({
//     type: "success",
//     message: "Missing documents email sent successfully!",
//   });
//   setTimeout(() => setNotification(null), 3000);
// };

// // Prepare missing fields for the modal using useMemo to prevent unnecessary re-renders
// const missingFields = useMemo(() => {
//   let fields = [];

//   // Ensure missingFields is always an array of strings
//   if (Array.isArray(casestatusreason)) {
//     fields = casestatusreason.map(f => String(f).trim());
//   } else if (typeof casestatusreason === "string" && casestatusreason.trim()) {
//     fields = [casestatusreason.trim()];
//   }

//   return fields;
// }, [casestatusreason]);

  return (
    <>
      {store.navigationHistory.canGoBack && onBackClick && (
        <div className="back-button-container">
          <McIcon
            icon="chevron-left"
            size="24"
            onClick={onBackClick}
            className="back-button-icon"
            style={{ cursor: 'pointer' }}
          />
          <span 
            onClick={onBackClick}
            className="back-button-text"
            style={{ cursor: 'pointer', marginLeft: '8px', fontSize: '14px', fontWeight: '500' }}
          >
            Back to Cases
          </span>
        </div>
      )}
      <div className="header-row">
        <div className="left-column">
          <header className="case-header">Details of Request #{id}</header>
           {/* Last email preview and view historical emails link */}
          <div className="last-email-container" style={{ marginTop: 8 }}>
            {messagesLoading ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <div style={{ fontSize: 12, color: '#666' }}>Loading latest email...</div>
              </div>
            ) : lastEmail ? (
              <div style={{ fontSize: 12, color: '#333' }}>
                <div><strong>Last Email Sent:</strong> {lastEmailDateTime}</div>
                <div><strong>From:</strong> AFRDigitalAssistant@maersk.com{lastEmail.bcc ? `(${lastEmail.bcc})` : ''} | <strong>To:</strong> {lastEmail.to ?? lastEmail.to_recipients ?? 'N/A'}</div>
                <div style={{ marginTop: 6 }}>
                  {/* View Historical Emails link commented out per request.
                  <a href="#" onClick={async (e) => { e.preventDefault(); e.stopPropagation();
                      try {
                        setMessagesLoading(true);
                        setMessagesData(null);
                        setMessagesModalOpen(true);
                        const resp = await fetch(`${API_URL}/get_case_messages?casenumber=${encodeURIComponent(id)}`);
                        if (!resp.ok) {
                          const text = await resp.text().catch(() => resp.statusText || 'error');
                          throw new Error(`Failed to fetch messages: ${resp.status} ${text}`);
                        }
                        const json = await resp.json();
                        const messages = Array.isArray(json.messages) ? json.messages.slice() : [];
                        const getMessageTime = (m) => {
                          const t = m?.receive_timestamp || m?.createddate || m?.receive_time || m?.timestamp || m?.datetime;
                          const d = t ? new Date(String(t)) : new Date(0);
                          return isNaN(d.getTime()) ? new Date(0) : d;
                        };
                        messages.sort((a, b) => getMessageTime(b) - getMessageTime(a));
                        setMessagesData({ casenumber: id, messages });
                      } catch (err) {
                        console.error('Error fetching case messages:', err);
                        setMessagesData({ casenumber: id, messages: [], error: err.message || String(err) });
                      } finally {
                        setMessagesLoading(false);
                      }
                    }}
                  >View Historical Emails</a> */}
                </div>
              </div>
            ) : (
              <div style={{ fontSize: 12, color: '#707070' }}>No emails sent yet</div>
            )}
          </div>
          {/* <p className="created-date">
            {createddate ? formatDateAndTime(createddate) : null}
          </p> */}
        </div>
        <div className="right-column">
          <div style={{ fontSize: 12, color: '#333' }}>
            <a href="#" onClick={(e) => { e.preventDefault(); fetchCaseHistory(); }}>
              View Historical Status
            </a>
          </div>
          <div className="status-wrapper">
            <div className={status} slot="trigger">
              <McTag fit="small">{`Status - ${status}`}</McTag>
            </div>
          </div>
          {lastModifiedBy && status.trim().toUpperCase() !== 'OPEN' && (
            <div style={{ fontSize: 12 }}>(Last Modified By: {lastModifiedBy})</div>
          )}
          {casestatusreason && (
            <div className="status-reason-container">
              {renderWithTooltip(casestatusreason, 3, true)}
            </div>
          )}         
        </div>
      </div>
      <div className="divider" />
      {notification && (
      <McNotification
        type={notification.type}
        body={notification.message}
        position="top-center"
        autoclose
        closable
        style={{ backgroundColor: 'green'}}
      />
    )}

    {/* <EmailPreviewModal
      isOpen={isEmailModalOpen}
      onClose={handleEmailModalClose}
      caseNumber={id}
      missingFields={missingFields}
      onEmailSent={handleEmailSent}
    /> */}

    {/* Use shared MessagesModal component */}
    <MessagesModal
      open={messagesModalOpen}
      onClose={() => { setMessagesModalOpen(false); setMessagesData(null); }}
      caseNumber={id}
      initialData={messagesData}
    />
    {/* History modal */}
    <CaseHistoryModal
      open={historyModalOpen}
      onClose={() => { setHistoryModalOpen(false); setHistoryData(null); }}
      selectedCaseNumber={id}
      historyData={historyData}
      historyLoading={historyLoading}
      formatDateTime={formatDateTime}
      setHistoryLoading={setHistoryLoading}
      setUnreadModalData={setUnreadModalData}
      setUnreadModalOpen={setUnreadModalOpen}
      setEmailViewData={setEmailViewData}
      setEmailViewOpen={setEmailViewOpen}
  hideEmailColumns
  width="70vw"
  height="60vh"
    />
    {/* Messages modal triggered from history table (unread conversation) */}
    <MessagesModal
      open={unreadModalOpen}
      onClose={() => { setUnreadModalOpen(false); setUnreadModalData(null); }}
      caseNumber={unreadModalData?.casenumber}
      initialData={unreadModalData}
    />
    {/* Email view modal for specific historical email activity */}
    <EmailViewModal
      open={emailViewOpen}
      onClose={() => { setEmailViewOpen(false); setEmailViewData(null); }}
      caseNumber={emailViewData?.casenumber}
      initialData={emailViewData}
    />
    </>
  );
};

export default CaseDetailsHeader;
 